// A react component that displays a background image with options to position the image vertically. The image does not repeat and is certain to cover the entire screen.

import React from 'react';
import { Box } from '../../base/Box/Box';
import { StoryblokImage } from '../../base/StoryblokImage/StoryblokImage';
import { BackgroundSectionStoryblok } from '../../types/storyblok';
import { tw, variants } from 'classname-variants';
import classNames from 'classnames';

const bkgLayoutVariants = variants({
  variants: {
    verticalPosition: {
      centre: tw`items-center`,
      above: tw`items-center`,
      farAbove: tw`items-center`,
      below: tw`items-center`,
      farBelow: tw`items-center`,
    },
    horizontalPosition: {
      centre: tw`justify-center`,
      left: tw`justify-start`,
      farLeft: tw`justify-start`,
      right: tw`justify-end`,
      farRight: tw`justify-end`,
    },
  },
});

const bkgPositioningVariants = variants({
  variants: {
    size: {
      sm: tw`!w-[calc((1rem_/_4)_*_120)]`, // 600px
      md: tw`!w-[calc((1rem_/_4)_*_160)]`, // 800px
      lg: tw`!w-[calc((1rem_/_4)_*_216)]`, // 1080px
      xl: tw`!w-[calc((1rem_/_4)_*_240)]`, // 1200px
    },
    verticalPosition: {
      centre: tw``,
      above: tw`-translate-y-[calc((1rem_/_4)_*_60)]`,
      farAbove: tw`-translate-y-[calc((1rem_/_4)_*_120)]`,
      below: tw`translate-y-[calc((1rem_/_4)_*_60)]`,
      farBelow: tw`translate-y-[calc((1rem_/_4)_*_120)]`,
    },
    horizontalPosition: {
      centre: tw``,
      left: tw`-translate-x-1/2`,
      farLeft: tw`-translate-x-[calc(50%_+_(1rem_/_4)_*_60)]`,
      right: tw`translate-x-1/2`,
      farRight: tw`translate-x-[calc(50%_+_(1rem_/_4)_*_60)]`,
    },
  },
});

type BackgroundSectionProps = BackgroundSectionStoryblok & {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  verticalPosition?: 'farAbove' | 'above' | 'centre' | 'below' | 'farBelow';
  horizontalPosition?: 'farLeft' | 'left' | 'centre' | 'right' | 'farRight';
};

export const BackgroundSection = ({
  image,
  size = 'sm',
  verticalPosition = 'centre',
  horizontalPosition = 'centre',
}: BackgroundSectionStoryblok) => {
  const maxImageSize = () => {
    switch (size) {
      case 'sm':
        return 600;

      case 'md':
        return 800;
      case 'lg':
        return 1080;

      case 'xl':
        return 1200;

      default:
        return 1200;
    }
  };

  return (
    <Box as="section" className="pointer-events-none relative overflow-x-clip">
      <Box
        as="div"
        className={classNames(
          'relative mx-auto flex h-0 w-full max-w-standard',
          bkgLayoutVariants({
            verticalPosition,
            horizontalPosition,
          })
        )}
      >
        <Box
          as="div"
          className={classNames(
            bkgPositioningVariants({
              size,
              verticalPosition,
              horizontalPosition,
            })
          )}
        >
          <StoryblokImage
            src={image}
            mode="responsive"
            maxWidth={maxImageSize()}
            alt=""
            bkgOrb
          />
        </Box>
      </Box>
    </Box>
  );
};

export default BackgroundSection;
